import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Button,
  useDisclosure,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
} from "@nextui-org/react";

import { clientsAPI, usersAPI } from "../services/api";

import DefaultLayout from "@/layouts/default";
import { EyeIcon, EditIcon, DeleteIcon } from "@/components/icons";

interface Client {
  id: string;
  name: string;
  email: string;
  address: string;
  subdomain: string;
  createdAt?: string;
}

const ClientsPage: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, _setCurrentUserRole] = useState<string>(
    localStorage.getItem("role") || "",
  );
  const isSuperAdmin = currentUserRole === "superadmin";

  // Form state for new client
  const [newClientName, setNewClientName] = useState("");
  const [newClientEmail, setNewClientEmail] = useState("");
  const [newClientAddress, setNewClientAddress] = useState("");
  const [formSubmitting, setFormSubmitting] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    if (!isSuperAdmin) {
      return;
    }

    setLoading(true);
    try {
      const clientsData = await clientsAPI.getAll();

      // Validate that each client has an id to prevent "No key found for item" error
      const validClients = clientsData.filter((client: any) => {
        if (!client || !client.id) {
          console.error("Invalid client data encountered:", client);

          return false;
        }

        return true;
      });

      setClients(validClients);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setError(t("Failed to fetch clients"));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewClientName("");
    setNewClientEmail("");
    setNewClientAddress("");
    setError(null);
  };

  const handleAddClient = async () => {
    if (!isSuperAdmin) {
      setError(t("Only superadmins can create clients"));

      return;
    }

    if (!newClientName || !newClientEmail || !newClientAddress) {
      setError(t("All fields are required"));

      return;
    }

    setFormSubmitting(true);

    try {
      // Create the new client
      const response = await clientsAPI.create({
        name: newClientName,
        email: newClientEmail,
        address: newClientAddress,
      });

      // Extract client data from response - API returns {client: {...}, message: "..."}
      const newClient = response.client || response;

      // Validate that the new client has an id to prevent "No key found for item" error
      if (!newClient || !newClient.id) {
        console.error("Invalid client data received from API:", response);
        throw new Error("Received invalid client data from server");
      }

      // Also create a default admin user for this client
      try {
        await usersAPI.create({
          name: `${newClientName} Admin`,
          email: newClientEmail,
          password: "admin@123", // Default password for all client admins
          password_confirmation: "admin@123",
          role: "admin",
          client_id: newClient.id,
        });

        console.log(
          `Created default admin user for client "${newClientName}" with email "${newClientEmail}"`,
        );
      } catch (userError: any) {
        // If user creation fails, log it but don't fail the entire operation
        console.error("Error creating default admin user:", userError);
        console.warn(
          `Default admin user creation failed for client ID ${newClient.id}, but client was still created successfully`,
        );
        // You might want to show a warning to the user
        // setError(t("Client created, but default admin user creation failed. Please create a user manually."));
      }

      // Add the client to the list
      setClients((prev) => [...prev, newClient]);

      // Close the modal and reset the form
      resetForm();
      onClose();
    } catch (error: any) {
      console.error("Error creating client:", error);
      setError(
        t("Failed to create client: ") +
          (error.response?.data?.error || error.message),
      );
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleView = (id: string) => {
    navigate(`/client/view/${id}`);
  };

  const handleEdit = (id: string) => {
    navigate(`/client/edit/${id}`);
  };

  const handleDelete = async (id: string) => {
    if (!isSuperAdmin) {
      setError(t("Only superadmins can delete clients"));

      return;
    }

    if (
      confirm(
        t(
          "Are you sure you want to delete this client? This action cannot be undone.",
        ),
      )
    ) {
      try {
        await clientsAPI.delete(id);
        // Remove the client from the list
        setClients((prev) => prev.filter((client) => client.id !== id));
      } catch (error) {
        console.error("Error deleting client:", error);
        setError(t("Failed to delete client"));
      }
    }
  };

  const renderCell = (
    client: Client,
    columnKey: keyof Client | "actions" | "serialNumber",
  ) => {
    if (columnKey === "actions") {
      return (
        <div className="relative flex justify-center items-center gap-2">
          <Tooltip content={t("View Client")}>
            <Button
              isIconOnly
              aria-label={t("View Client")}
              size="sm"
              variant="light"
              onClick={() => handleView(client.id)}
            >
              <EyeIcon />
            </Button>
          </Tooltip>
          <Tooltip content={t("Edit Client")}>
            <Button
              isIconOnly
              aria-label={t("Edit Client")}
              size="sm"
              variant="light"
              onClick={() => handleEdit(client.id)}
            >
              <EditIcon />
            </Button>
          </Tooltip>
          <Tooltip color="danger" content={t("Delete Client")}>
            <Button
              isIconOnly
              aria-label={t("Delete Client")}
              color="danger"
              size="sm"
              variant="light"
              onClick={() => handleDelete(client.id)}
            >
              <DeleteIcon />
            </Button>
          </Tooltip>
        </div>
      );
    }
    if (columnKey === "serialNumber") {
      const index = clients.findIndex((c) => c.id === client.id) + 1;

      return <span>{index}</span>;
    }

    return client[columnKey] as any;
  };

  const columns = [
    { uid: "serialNumber", name: t("S.No") },
    { uid: "name", name: t("Name") },
    { uid: "email", name: t("Email") },
    { uid: "address", name: t("Address") },
    { uid: "actions", name: t("Actions") },
  ];

  // If not superadmin, show access denied
  if (!isSuperAdmin) {
    return (
      <DefaultLayout>
        <div className="p-4">
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p className="font-bold">{t("Access Denied")}</p>
            <p>{t("Only superadmins can manage clients.")}</p>
          </div>
          <Button color="primary" onPress={() => navigate("/")}>
            {t("Go to Home")}
          </Button>
        </div>
      </DefaultLayout>
    );
  }

  return (
    <DefaultLayout>
      <div className="p-4">
        <h1 className="text-3xl font-bold text-center mb-8">{t("Clients")}</h1>

        {error && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4">
            <p>{error}</p>
          </div>
        )}

        <div className="mb-4">
          <Button color="primary" onPress={onOpen}>
            {t("Add New Client")}
          </Button>

          <Modal
            isOpen={isOpen}
            onClose={() => {
              resetForm();
              onClose();
            }}
          >
            <ModalContent>
              <ModalHeader>{t("Add New Client")}</ModalHeader>
              <ModalBody>
                <div className="space-y-4">
                  <Input
                    isRequired
                    label={t("Name")}
                    placeholder={t("Enter client name")}
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                  />
                  <Input
                    isRequired
                    label={t("Email")}
                    placeholder={t("Enter client email")}
                    value={newClientEmail}
                    onChange={(e) => setNewClientEmail(e.target.value)}
                  />
                  <Input
                    isRequired
                    label={t("Address")}
                    placeholder={t("Enter client address")}
                    value={newClientAddress}
                    onChange={(e) => setNewClientAddress(e.target.value)}
                  />
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color="danger"
                  variant="flat"
                  onPress={() => {
                    resetForm();
                    onClose();
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  color="primary"
                  isDisabled={
                    !newClientName || !newClientEmail || !newClientAddress
                  }
                  isLoading={formSubmitting}
                  onPress={handleAddClient}
                >
                  {t("Create Client")}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </div>

        {loading ? (
          <p>{t("Loading clients...")}</p>
        ) : (
          <div className="overflow-x-auto">
            <Table aria-label={t("Clients Table")}>
              <TableHeader columns={columns}>
                {(column) => (
                  <TableColumn
                    key={column.uid}
                    align={column.uid === "actions" ? "center" : "start"}
                  >
                    {column.name}
                  </TableColumn>
                )}
              </TableHeader>
              <TableBody
                emptyContent={t("No clients found")}
                items={clients.map((client) => ({
                  key: client.id,
                  ...client,
                }))}
              >
                {(client) => (
                  <TableRow key={client.id}>
                    {(columnKey) => (
                      <TableCell>
                        {renderCell(
                          client,
                          columnKey as
                            | keyof Client
                            | "actions"
                            | "serialNumber",
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </DefaultLayout>
  );
};

export default ClientsPage;
